import React from 'react';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { SS } from '../../config/styles';
import { apiUrl } from '../../config/supabase';

export const Alert = ({ msg, type }) => (
  <div style={{ background: type === 'error' ? '#FEF2F2' : '#F0FDF4', border: `1px solid ${type === 'error' ? '#FECACA' : '#BBF7D0'}`, borderRadius: '10px', padding: '10px 14px', color: type === 'error' ? '#DC2626' : '#16A34A', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
    {type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />} {msg}
  </div>
);

// 📱 Overlay/Modal متجاوبين مع الموبايل: الغلاف بياخد عرض الشاشة كله، والمودال مش بيعدّي حدود الشاشة
export const Overlay = ({ children, onClose, dark }) => (
  <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: '12px', boxSizing: 'border-box', backdropFilter: 'blur(6px)', background: dark ? 'rgba(5,15,40,0.9)' : 'rgba(0,0,0,0.55)', overflowY: 'auto' }} onClick={onClose}>
    <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>{children}</div>
  </div>
);

export const Modal = ({ children, dark, maxWidth = '500px' }) => (
  <div style={{ background: dark ? '#0f172a' : 'white', borderRadius: '24px', padding: 'clamp(16px, 4vw, 36px)', width: '100%', maxWidth, maxHeight: '92vh', overflowY: 'auto', overflowX: 'hidden', boxSizing: 'border-box', boxShadow: '0 25px 60px rgba(0,0,0,0.35)' }}>
    {children}
  </div>
);

export const Spin = ({ color = 'white', size = 32 }) => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
    <Loader size={size} color={color} style={SS} />
  </div>
);

// Google Drive Player
export const DrivePlayer = ({ fileId }) => {
  const id = (fileId || '').match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] || fileId;
  if (!id) return null;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '16px', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
      <iframe src={`https://drive.google.com/file/d/${id}/preview`} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }} allow="autoplay" allowFullScreen title="video" />
      <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 'calc(100% - 40px)', pointerEvents: 'none', zIndex: 2 }} />
    </div>
  );
};

// Vimeo Player — يقبل رابط كامل أو ID أو ID/hash
export const VimeoPlayer = ({ vimeoId }) => {
  const raw = (vimeoId || '').trim();
  if (!raw) return null;
  const match = raw.match(/vimeo\.com\/(\d+(?:\/[a-f0-9]+)?)/i);
  const id = match ? match[1] : raw;
  const [numId, hash] = id.split('/');
  const src = hash
    ? `https://player.vimeo.com/video/${numId}?h=${hash}&color=667eea&byline=0&portrait=0&title=0`
    : `https://player.vimeo.com/video/${numId}?color=667eea&byline=0&portrait=0&title=0`;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '16px', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
      <iframe src={src} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="video" />
    </div>
  );
};

// Bunny Stream Player — يقبل رابط embed كامل أو "libraryId/videoGuid"
export const BunnyPlayer = ({ code }) => {
  const raw = (code || '').trim();
  if (!raw) return null;
  let path = raw;
  const m = raw.match(/mediadelivery\.net\/(?:embed|play)\/([^?"'\s]+)/i);
  if (m) path = m[1];
  path = path.replace(/^\/+|\/+$/g, '');
  if (!path) return null;
  const src = `https://iframe.mediadelivery.net/embed/${path}?preload=true&responsive=true`;
  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '16px', background: '#000', userSelect: 'none' }} onContextMenu={e => e.preventDefault()}>
      <iframe src={src} loading="lazy" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen title="video" />
    </div>
  );
};

// ✅ VideoPlayer الذكي — لو فيه vimeo_id يشغّل Vimeo، لو لأ يشغّل Drive
export const VideoPlayer = ({ lesson }) => {
  const [video, setVideo] = React.useState(null);
  const [phase, setPhase] = React.useState('loading'); // loading | ready | denied | error

  React.useEffect(() => {
    if (!lesson || !lesson.id) { setPhase('error'); return undefined; }
    let alive = true;
    setPhase('loading'); setVideo(null);
    let token = '';
    try { token = localStorage.getItem('cust_token') || ''; } catch (e) {}
    fetch(apiUrl('/api/lesson/video'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, lessonId: lesson.id }),
    })
      .then((res) => res.json().then((j) => ({ ok: res.ok, j })))
      .then(({ ok, j }) => {
        if (!alive) return;
        if (ok && j && j.ok && j.video) { setVideo(j.video); setPhase('ready'); }
        else if (j && j.error === 'session_expired') {
          setPhase('expired');
          try { window.dispatchEvent(new CustomEvent('cust-session-expired')); } catch (e) {}
        }
        else if (j && j.error === 'forbidden') { setPhase('denied'); }
        else { setPhase('error'); }
      })
      .catch(() => { if (alive) setPhase('error'); });
    return () => { alive = false; };
  }, [lesson && lesson.id]);

  if (!lesson) return null;

  const Box = ({ children }) => (
    <div style={{ position: 'relative', paddingTop: '56.25%', background: '#0f172a', borderRadius: '12px', overflow: 'hidden' }}>
      <style>{'@keyframes gwspin{to{transform:rotate(360deg)}}'}</style>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '10px', color: '#cbd5e1', fontSize: '14px', textAlign: 'center', padding: '16px' }}>
        {children}
      </div>
    </div>
  );

  if (phase === 'loading') return <Box><Loader size={26} style={{ animation: 'gwspin 1s linear infinite' }} /><span>جاري تحميل الفيديو…</span></Box>;
  if (phase === 'expired') return <Box><span style={{ fontSize: '28px' }}>⏰</span><span>انتهت جلستك — سجّل دخول تاني وهتلاقي الحلقة شغّالة</span></Box>;
  if (phase === 'denied')  return <Box><span style={{ fontSize: '28px' }}>🔒</span><span>لازم تشترك في الكورس الأول عشان تتفرّج على الحلقة</span></Box>;
  if (phase === 'error' || !video) return <Box><AlertCircle size={24} /><span>تعذّر تحميل الفيديو، حاول تاني</span></Box>;

  const src = (video.video_source || '').toLowerCase();
  if (src === 'bunny' && video.bunny_video_id) return <BunnyPlayer code={video.bunny_video_id} />;
  if (src === 'vimeo' && video.vimeo_id)        return <VimeoPlayer vimeoId={video.vimeo_id} />;
  if (src === 'drive' && video.drive_file_id)   return <DrivePlayer fileId={video.drive_file_id} />;
  if (video.vimeo_id)       return <VimeoPlayer vimeoId={video.vimeo_id} />;
  if (video.bunny_video_id) return <BunnyPlayer code={video.bunny_video_id} />;
  return <DrivePlayer fileId={video.drive_file_id} />;
};

// ✅ SmartIntro — يستقبل قيمة نصية واحدة (رابط Vimeo أو ID أو ملف Drive)
// ويكتشف المصدر أوتوماتيك. بيتستخدم للفيديو التعريفي/المعاينة.
export const SmartIntro = ({ src }) => {
  const s = (src || '').trim();
  if (!s) return null;
  const isVimeo = /vimeo\.com/i.test(s) || /^\d{5,}(?:\/[a-z0-9]+)?$/i.test(s);
  return isVimeo ? <VimeoPlayer vimeoId={s} /> : <DrivePlayer fileId={s} />;
};

export const PermsGrid = ({ state, setState, permLabels, PERM_KEYS }) => (
  <div style={{ background: '#1e293b', borderRadius: '12px', padding: '14px', border: '1px solid #334155' }}>
    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#94a3b8', marginBottom: '10px' }}>🛡️ الصلاحيات</div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
      {PERM_KEYS.map(k => {
        const isOn = state[k] || false;
        return (
          <div key={k} onClick={() => setState(p => ({ ...p, [k]: !p[k] }))}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: isOn ? '#1e3a5f' : '#0f172a', border: `1px solid ${isOn ? '#667eea' : '#334155'}`, borderRadius: '8px', padding: '10px 12px', cursor: 'pointer', transition: 'all .2s', userSelect: 'none' }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `2px solid ${isOn ? '#667eea' : '#334155'}`, background: isOn ? '#667eea' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .2s' }}>
              {isOn && <span style={{ color: 'white', fontSize: '11px', fontWeight: 'bold' }}>✓</span>}
            </div>
            <span style={{ fontSize: '12px', color: isOn ? '#93c5fd' : '#94a3b8', userSelect: 'none' }}>{permLabels[k]}</span>
          </div>
        );
      })}
    </div>
  </div>
);
